import { create } from 'zustand';
import { appConfig } from '../config/app-config';
import { directClient } from '../api/http';
import { HttpFailure } from '../api/http-failure';
import {
  adoptStoredPreferences,
  currentPreferences,
  loadPreferences,
  onPreferencesSaved,
  resetPreferencesToFallback,
  theme as preferredTheme,
} from '../preferences/preferences.store';
import { isOnline } from '../pwa/connectivity';
import { applyTheme } from '../theme/theme-controller';
import { createLockRunner, type LockRunner } from '../concurrency/browser-lock';
import { noteOutcome, subscribeToBeat } from '../reach/reach.store';
import { clearEveryStoredView, clearStoredViewsFor } from '../stored-views/device-views';
import { SESSION_TIMEOUT_MS } from './session-timeout';
import type { AccountSummary, PreferencesSummary, SignInRequest, TokenPair } from '../api/types';

/**
 * 036 `session.store.ts` MUST NOT import from `core/outbox/`. It used to (for
 * `signOut`'s "send what can be sent first"), and `core/outbox/outbox.store.ts` imports `account`
 * and `isSignedIn` back from here — a real circular import between the two modules. It worked
 * (nothing here is read at module-evaluation time, only inside functions called later), but it was
 * a decision made under time pressure rather than a clean one, flagged honestly in the pull
 * request that introduced it. The orchestration of "sign out with unsent items" now lives in
 * `core/session/sign-out.ts`, which is free to import both directions; this file keeps only what
 * concerns the session itself, and exports `signOutNow` as the one primitive that ends a session
 * without knowing the outbox exists.
 */

/**
 * 036 (issue 219, research R-003) — `unconfirmed` is the fourth: a refresh token is held, the
 * server could not be ASKED (never "was asked and refused" — that is `signed-out`), and the
 * practitioner is the one last CONFIRMED. In this state `account` is non-null, the preferences held
 * are the ones last confirmed (time zone, date style, theme all in effect), `refreshToken` is held,
 * and `accessToken` is `null`.
 *
 * It is reached two ways: `restore()`'s fast path, when a stored token and a stored identity pair
 * (nothing has been asked yet — this is the FIRST attempt, not a degraded one), and `standDown()`,
 * when a renewal for a session already held here could not be asked. It is left three ways: a
 * renewal succeeds (`signed-in`, through `adopt`), a renewal is genuinely REFUSED (`clear()` —
 * `signed-out`), or another tab's identity write names a different practitioner or removes it
 * (`endInMemoryOnly()` — `signed-out`).
 */
export type SessionStatus = 'unknown' | 'signed-out' | 'signed-in' | 'unconfirmed';

interface SessionState {
  /** In memory only. Short-lived (003 FR-009) and re-minted by restore(), so persisting it would add exposure and buy nothing. */
  accessToken: string | null;
  refreshToken: string | null;
  account: AccountSummary | null;
  /** `unknown` until the startup restore attempt settles. */
  status: SessionStatus;
}

const initial: SessionState = { accessToken: null, refreshToken: null, account: null, status: 'unknown' };

const store = create<SessionState>()(() => ({ ...initial }));

/**
 * 034: the same pairing the preferences store uses — a `use…` form for components and a plain one
 * for the guards, the interceptor and the stores, which read from outside React.
 */
const select = <T,>(pick: (s: SessionState) => T) => ({
  use: (): T => store(pick),
  get: (): T => pick(store.getState()),
});

export const status = select((s) => s.status);
/**
 * 036 — narrow ON PURPOSE. `unconfirmed` is never treated as signed in by anything that SENDS:
 * `core/outbox/outbox.store.ts`'s `stillMine` and `core/http/renewing-client.ts`'s retry gate both
 * read this, and FR-008's "the session is confirmed" is exactly the line this draws. A reader that
 * wants "is there a session to draw a screen for" wants `hasSession` below instead.
 */
export const isSignedIn = select((s) => s.status === 'signed-in');
/**
 * 036 (issue 219) — "has a session worth drawing a screen for": `signed-in` or `unconfirmed`, never
 * `signed-out` or `unknown`. `RequireSignedIn`/`RequireAnonymous`/`RequireLinkToken` in `guards.tsx`
 * use this — an unconfirmed practitioner is exactly as entitled to see their own screen, and exactly
 * as uninterested in the sign-in form, as a signed-in one. Nothing that SENDS may use this in place
 * of `isSignedIn`.
 */
export const hasSession = select((s) => s.status === 'signed-in' || s.status === 'unconfirmed');

/**
 * `core/outbox/`'s trigger for "the session becoming signed-in" (research R-007).
 * A generic primitive rather than anything outbox-shaped: this file does not know the outbox
 * exists, and does not need to for a caller elsewhere to react to ITS OWN transitions. Fires only
 * on the EDGE into `signed-in`, never on every state change — a subscriber that wants "wake up and
 * try sending" has no use for "became unknown" or "became signed-out".
 */
export function onSignedIn(callback: () => void): () => void {
  return store.subscribe((state, previous) => {
    if (state.status === 'signed-in' && previous.status !== 'signed-in') callback();
  });
}

/**
 * Fires whenever the practitioner the outbox belongs to changes: a new one arrives, or none does.
 * `core/outbox/` uses this to know when its channel, its open database handle and its mirror stop
 * being this practitioner's. Compares practitioner IDS, not account object identity or status
 * alone, so an ordinary renewal — same practitioner, new tokens — fires nothing: nothing about WHOSE
 * outbox it is has changed.
 *
 * 036 (issue 219): `unconfirmed` counts as "this practitioner's" here, exactly like `signed-in` —
 * `standDown()` KEEPS the account, and the outbox must stay open through it (a capture typed while
 * unconfirmed is still written to the device, FR-006). Only `signed-out`/`unknown` read as "no
 * practitioner", so the outbox closes on a genuine sign-out or refusal, never on a mere loss of
 * confirmation.
 */
export function onPractitionerChange(callback: (practitionerId: number | string | null) => void): () => void {
  const idOf = (s: SessionState): number | string | null =>
    s.status === 'signed-in' || s.status === 'unconfirmed' ? (s.account?.practitionerId ?? null) : null;
  return store.subscribe((state, previous) => {
    const next = idOf(state);
    if (next !== idOf(previous)) callback(next);
  });
}

export const isSettled = select((s) => s.status !== 'unknown');
export const loginName = select((s) => s.account?.loginName ?? null);
export const account = select((s) => s.account);
export const accessToken = select((s) => s.accessToken);
/**
 * What the admin-only surfaces ask (the person menu, the admin guard). Convenience, never
 * enforcement: it reads the cached account, and the routes it selects re-read standing server-side
 * per request (014 R-013).
 */
export const isAdmin = select((s) => s.account?.isAdmin === true);
/**
 * 014 FR-029: what the toolbar calls you — the first name once one exists, the login name until
 * then. Bound to `loginName` the toolbar could never follow a rename, because that field is
 * FR-026's one immutable; this moves the moment /account's save hands the server's answer to
 * `adoptAccount`, with no reload and no sign-out.
 */
export const displayName = select((s) => (s.account ? (s.account.firstName ?? s.account.loginName) : null));

const url = (path: string): string => `${appConfig().apiBaseUrl}${path}`;
const storageKey = (): string => `${appConfig().storagePrefix}refreshToken`;
const identityKey = (): string => `${appConfig().storagePrefix}identity`;

/**
 * 036 R-003 — "as last confirmed": the account summary and the practitioner's preferences, kept on
 * the device so a later cycle's `unconfirmed` state can draw a screen and name a time zone without
 * a session the server has answered. This pull request only WRITES it; nothing yet reads it back —
 * `restore()` still settles from the network exactly as it did before this cycle, and the
 * `unconfirmed` status itself is Phase 2's (T058), not this one's.
 *
 * The database `core/outbox/` opens is also NAMED by this record's `practitionerId` — see
 * `submitCapture()` — so an identity that outlives the session it was read from is what lets the
 * outbox find the right database before the account is fetched again.
 *
 * `tokenHash` is what makes the stored token and the stored identity a
 * PROVABLE pair rather than two writes that merely happened close together. `adopt()` used to write
 * the refresh token, then (after awaiting the account/preferences reads) write the identity —
 * ordered so a stale identity is never left beside a FRESH token, but nothing stopped the reverse: a
 * SECOND window's `adopt()` interleaving its own identity write between this window's token write
 * and this window's (slower) identity write left practitioner A's identity on top of practitioner
 * B's token. `renewOnce` trusted the pairing because the fields agreed on paper; it never asked
 * whether they were written BY THE SAME `adopt()` call. A hash of the token INSIDE the identity
 * record answers that: it can only match the token `adopt()` was writing at the same moment it
 * computed the hash, so a mismatch — however it arose — is treated exactly like "signed out
 * elsewhere," which `renewOnce` already handles safely.
 */
interface StoredIdentity {
  readonly account: AccountSummary;
  readonly preferences: PreferencesSummary;
  /**
   * The pairing hash, prefixed with the algorithm that produced it (`sha256:…` or `fnv1a:…` — see
   * `hashToken`). `null` or missing is a MISMATCH, full stop — never a weakened fallback to the
   * practitioner id alone. The pairing must never go unchecked merely because a context could not
   * compute a hash at the moment it was written: `hashToken` below always returns something now, so
   * a `null` here can only mean a torn write, a hand-edited value, or a record from before this
   * fix — none of which this window may safely treat as "the same token".
   */
  readonly tokenHash: string | null;
}

type HashAlgorithm = 'sha256' | 'fnv1a';

/**
 * Which algorithm THIS context can compute right now. `sha256` wherever `crypto.subtle` exists
 * (every secure context); `fnv1a` — synchronous, non-cryptographic, 64 bits of output — everywhere
 * else (the compose stack reached by a LAN address, for one, where `crypto.subtle` is undefined
 * outright, not merely slow or refusing).
 */
function currentHashAlgorithm(): HashAlgorithm {
  return typeof crypto.subtle?.digest === 'function' ? 'sha256' : 'fnv1a';
}

/**
 * FNV-1a, 64-bit, hex-encoded. Not a security boundary — see `hashToken`'s own comment — merely a
 * digest that is available EVERYWHERE `hashToken` runs, so the pairing check is never left with
 * nothing to compare. 32 bits was rejected as too small a space for "detect a mismatch" to mean much;
 * 64 (the offset basis and prime below are the standard 64-bit FNV-1a constants) is plenty for a
 * check that never faces an adversary — the token itself sits in the clear right beside it.
 */
function fnv1aHex(token: string): string {
  const MASK64 = (1n << 64n) - 1n;
  const PRIME = 1099511628211n;
  let hash = 14695981039346656037n; // FNV offset basis, 64-bit
  for (const byte of new TextEncoder().encode(token)) {
    hash = ((hash ^ BigInt(byte)) * PRIME) & MASK64;
  }
  return hash.toString(16).padStart(16, '0');
}

/** The raw digest only, no algorithm prefix — `null` only when `algorithm` names `sha256` and this
 *  context cannot compute it (asked for the algorithm a STORED value named, not the one this
 *  context would have picked itself; see `hashMatches`). */
async function computeDigest(token: string, algorithm: HashAlgorithm): Promise<string | null> {
  if (algorithm === 'fnv1a') return fnv1aHex(token);
  if (typeof crypto.subtle?.digest !== 'function') return null;
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * SHA-256 of the refresh token where Web Crypto exists, FNV-1a where it does not — hex-encoded and
 * prefixed with which one (`sha256:…` / `fnv1a:…`), never `null`. Not a security boundary (the token
 * is still stored in the clear beside it, exactly as before) — a PAIRING check, so a mismatch is
 * detectable at all, and now on every browser this application runs on rather than only the secure
 * ones. Before FNV-1a existed here, an insecure context made this resolve `null`, and the pairing
 * check downstream fell back to the practitioner id alone — a torn write naming the right
 * practitioner beside the wrong token went undetected on exactly the browsers that could least
 * afford it, since they had no stronger check available at all.
 *
 * Exported ONLY so a spec that simulates "another window renewed" can write a `tokenHash` that
 * actually matches the token it also writes — the same algorithm, not a second copy of it that
 * could quietly drift from this one. Every production caller is inside this file.
 */
export async function hashToken(token: string): Promise<string> {
  const algorithm = currentHashAlgorithm();
  const digest = await computeDigest(token, algorithm);
  return `${algorithm}:${digest ?? fnv1aHex(token)}`; // computeDigest cannot return null for the
  // algorithm currentHashAlgorithm() itself picked; the fallback is defensive only, so this can
  // never throw regardless of what a future change to either function does.
}

/**
 * Does `stored` name the SAME token as `token`, computed like with like? A stored value carries its
 * own algorithm (`sha256:…` / `fnv1a:…`); this recomputes using THAT algorithm — not necessarily the
 * one `currentHashAlgorithm()` would pick for a fresh write — so a browser that has since gained or
 * lost `crypto.subtle` can still verify a pairing written under the other one. `null`, missing, or
 * an algorithm this context cannot compute (asked for `sha256` where `crypto.subtle` does not
 * exist) is a MISMATCH — never a weaker pass.
 */
async function hashMatches(token: string, stored: string | null): Promise<boolean> {
  if (!stored) return false;
  const separator = stored.indexOf(':');
  if (separator < 0) return false;
  const algorithm = stored.slice(0, separator);
  if (algorithm !== 'sha256' && algorithm !== 'fnv1a') return false;
  const digest = await computeDigest(token, algorithm);
  return digest !== null && `${algorithm}:${digest}` === stored;
}

/**
 * Every storage read this file makes now says whether storage could be
 * READ AT ALL, not just what it held. `{ok: false}` — the read THREW (private browsing that blocks
 * `localStorage` entirely, blocked site data) — is a different fact from `{ok: true, value: null}`
 * — storage answered and there was nothing there, or nothing valid. `renewOnce`'s isolation check
 * could not tell the two apart before this: BOTH read as "no stored token," so a
 * window whose storage simply could not be read looked exactly like one signed out from elsewhere,
 * and ended its own live session over a browser condition that has nothing to do with who is
 * signed in. Only the SECOND case is safe to treat as "storage disagrees with memory"; the first
 * has nothing to disagree with and must fall back to the in-memory token, as every renewal did
 * before this cycle's isolation fix.
 */
type StorageRead<T> = { readonly ok: true; readonly value: T | null } | { readonly ok: false };

/** Storage access guarded exactly like the refresh token's (private browsing, blocked site data). */
function writeIdentity(identity: StoredIdentity): void {
  try {
    localStorage.setItem(identityKey(), JSON.stringify(identity));
  } catch {
    /* not being able to remember the identity is not a reason to fail the request in front of us */
  }
}

/** FR-046 — data-model.md: "removed at sign-out and at any refusal of the session." Not called by
 *  `standDown()`: a session that merely could not be CONFIRMED keeps its identity, the same way it
 *  keeps its refresh token, so an offline launch can still draw the practitioner's own screen. */
function clearIdentity(): void {
  try {
    localStorage.removeItem(identityKey());
  } catch {
    /* nothing to do if the browser will not let us remove it either */
  }
}

/**
 * A parseable value is not necessarily a USABLE one. Before this, `readIdentity`
 * cast whatever `JSON.parse` returned straight to `StoredIdentity` — `{}`, `null`-as-a-string's own
 * parse (`"null"` parses to the JS value `null`, already handled below, but `"{}"` parses to an
 * object with none of this shape's fields), or anything else valid JSON but not this shape, passed
 * through untouched. `renewOnce`'s own mismatch check then read
 * `storedIdentity.account.practitionerId` on a `storedIdentity` with no `account` at all, throwing
 * `TypeError: Cannot read properties of undefined` FROM INSIDE the renewal lock — the exact "throws
 * out of `renewOnce` entirely" failure the unparseable-JSON case just above this already guards
 * against, reachable by a value that parses fine and is simply the wrong shape. Deliberately narrow
 * (only `account`, the one field the mismatch check actually reads before anything else): this is
 * not a general schema validator, the same restraint `isKnownRecord` in `device-database.ts` states
 * for its own narrow purpose.
 */
function isStoredIdentity(value: unknown): value is StoredIdentity {
  if (!value || typeof value !== 'object') return false;
  const account = (value as Record<string, unknown>)['account'];
  return !!account && typeof account === 'object';
}

/**
 * Read back what `writeIdentity` wrote, so `renewOnce` can tell WHOSE token
 * storage is holding before it presents it to the server as this window's own. `{ok: false}` when
 * the read itself threw, distinct from a legitimately empty or unparseable value.
 */
function readIdentity(): StorageRead<StoredIdentity> {
  try {
    const raw = localStorage.getItem(identityKey());
    if (!raw) return { ok: true, value: null };
    try {
      const parsed = JSON.parse(raw) as unknown;
      // A value that parses but fails the shape check is treated exactly like
      // one that never parsed at all: not a usable identity, but storage itself answered fine.
      return { ok: true, value: isStoredIdentity(parsed) ? parsed : null };
    } catch {
      // Parseable-but-wrong and unparseable are the same fact here: not a usable identity, but
      // storage itself answered fine — never confused with "storage could not be read at all".
      return { ok: true, value: null };
    }
  } catch {
    return { ok: false };
  }
}

/** Browser storage can throw outright (private mode, blocked site data); a session that cannot be persisted still works for this page load. */
function readStored(): string | null {
  const read = readStoredTagged();
  return read.ok ? read.value : null;
}

/**
 * The tagged form `renewOnce` needs: whether storage could be read AT
 * ALL is a fact `readStored()`'s plain `string | null` cannot carry (both "unreadable" and
 * "readable, empty" collapse to `null`), and that collapse is exactly what made a renewal end an
 * otherwise-healthy session over blocked storage. `readStored()` above is unchanged in shape for
 * its other two callers (`restore()`, `signOutNow()`), which only ever wanted a fallback value and
 * are unaffected by the distinction.
 */
function readStoredTagged(): StorageRead<string> {
  try {
    const value = localStorage.getItem(storageKey());
    return { ok: true, value: value && value.length > 0 ? value : null };
  } catch {
    return { ok: false };
  }
}

function writeStored(value: string | null): void {
  try {
    if (value) localStorage.setItem(storageKey(), value);
    else localStorage.removeItem(storageKey());
  } catch {
    /* not being able to remember the session is not a reason to fail the request in front of us */
  }
}

/**
 * Could we not ASK, as opposed to were we refused? (021 FR-006b.)
 *
 * Status 0 is what the browser reports when a request never reached anything, and it is also what
 * the offline interceptor synthesises for the same condition — though note session traffic uses
 * `directClient` by design, so offline renewals fail at the network layer and arrive here as the
 * genuine article.
 *
 * Connectivity comes from `connectivity.ts`, not from `navigator.onLine` directly. That module is
 * declared as the ONE statement of this fact, and reading the global here would be a second one —
 * untestable besides, since a spec can stub the module and cannot easily control the global.
 *
 * This distinction is the same one 014 drew for the session-liveness check and 015 drew for a theme
 * word: THE ABSENCE OF AN ANSWER IS NOT A "NO". Without it, 021 introduces a data-destroying defect
 * that did not exist before it — see `standDown` below.
 */
const couldNotReach = (error: unknown): boolean =>
  (error instanceof HttpFailure && error.status === 0) || !isOnline();

/**
 * Moves on every event that ENDS or BEGINS a session in this tab: `clear()`, `standDown()`,
 * `endInMemoryOnly()`, `signOutNow()`, and the start of `signIn()`. A renewal in flight when it moves
 * was started FOR A SESSION THAT NO LONGER HOLDS — a sign-out that landed while its answer was still
 * on the wire, or a different practitioner signing in on the same tab in the meantime — and its
 * answer, however it arrives, is not this tab's current session's to adopt (`renewOnce` reads this
 * before asking the server and checks it again when the answer comes back).
 *
 * Deliberately bumped inside EACH of the five functions rather than only the ones that call the
 * others (`signOutNow()` always reaches `clear()`, `clear()` always reaches `standDown()`) — a
 * redundant bump costs nothing, since only INEQUALITY is ever tested, and it is what keeps a future
 * caller from being able to change what one of them does to the session without also moving this.
 */
let sessionEpoch = 0;

function bumpEpoch(): void {
  sessionEpoch += 1;
}

/**
 * 036 (issue 258, B2, FR-046) — `core/stored-views/storing-client.ts`'s own guard against writing a
 * late answer after a sign-out. A stored-view GET captures the practitioner it is for, and this
 * epoch, when it STARTS; its eventual device write re-checks this, fresh, at the moment the write
 * itself actually RUNS (queued writes can sit behind others). An epoch mismatch means this tab's
 * session has moved on since the read began — a sign-out, a refused renewal, or even the SAME
 * practitioner signing back in — any of which makes a write still carrying the OLD epoch not this
 * session's to finish, whatever the practitioner id in the answer happens to be.
 */
export function currentSessionEpoch(): number {
  return sessionEpoch;
}

/**
 * The shared "settle fully anonymous" body — every in-memory field wiped, the theme reset to the
 * one every anonymous surface wears (FR-010). Never touches storage; each caller below decides that
 * separately (`clear()` wipes it, `endInMemoryOnly()` and `standDown()`'s own no-account branch
 * leave it exactly as it is).
 */
function resetToSignedOut(): void {
  applyTheme('light');
  store.setState({ ...initial, status: 'signed-out' });
}

/**
 * Settle a renewal that could not be ASKED — never one that was refused, which is `clear()`'s.
 *
 * 036 (issue 219, research R-003): where this used to always settle `signed-out` while keeping the
 * stored token (021's fix, kept below), it now settles `unconfirmed` INSTEAD, whenever there is an
 * account already here to keep one for. The practitioner is the one LAST CONFIRMED: the account and
 * preferences already held (from a prior sign-in or renewal, or from `restore()`'s own fast path)
 * stay exactly as they are — the toolbar, the theme, the zone all stay in effect — and so does the
 * refresh token (021 FR-006b's reasoning: a server that could not be asked has not revoked
 * anything). Only the access token is retired, since a server that could not be asked cannot have
 * re-minted one.
 *
 * The no-account branch is the ORIGINAL 021 defect this function was written to fix, unchanged: an
 * offline launch with nothing trustworthy to fall back on (no paired identity — `restore()` never
 * took the fast path) has nothing an `unconfirmed` screen could show, so it settles `signed-out`
 * exactly as it always has, keeping only the stored token. Before a service worker, opening the
 * application with no network showed the browser's error page and ran no application code at all;
 * now it opens, `restore()` runs, its renewal cannot reach the server, and a bare `catch { clear() }`
 * would read that as "the server refused you" and null the stored token — the credential would be
 * gone even though nobody ever said so.
 *
 * 036 (issue 208): this used to also call `core/pwa/offline-deferrals.ts`'s `noteDeferral()`, for
 * `core/pwa/reconnect.ts`'s reload-on-reconnect to act on. Both are deleted (research R-009): the
 * unconfirmed session's own recovery (`startSessionRecovery()`, below) and `core/reach/`'s `resync`
 * are what bring a screen back to life on reconnect now, and neither needs a "something was
 * deferred" flag — `startSessionRecovery()`'s own beat subscription already costs nothing while
 * `status` is not `unconfirmed`.
 */
function standDown(): void {
  bumpEpoch();
  if (store.getState().account === null) {
    // The offline-LAUNCH case with nothing paired, or a warm renewal for a session this tab never
    // actually held.
    resetToSignedOut();
    return;
  }
  store.setState({ accessToken: null, status: 'unconfirmed' });
}

/**
 * End the session AND discard the credential. The one line here — wiping storage before settling —
 * is the whole difference between "we could not confirm you" (`standDown()`, which keeps everything)
 * and "you are signed out", and writing it this way is what stops the two drifting apart.
 *
 * 036 (issue 208, FR-046) — every stored view is deleted here too: `clear()` is reached from a
 * genuine sign-out (`signOutNow()`) and from a renewal genuinely REFUSED (`renewOnce()`'s catch),
 * which is exactly "every sign-out and any refusal of the session" — `standDown()` above is
 * deliberately NOT one of these call sites, since it settles `unconfirmed` rather than ending the
 * session, and FR-047 is what a stored view is FOR in that state. Best-effort and fire-and-forget:
 * a device that cannot be cleared is not a reason to fail, or even to slow down, ending the session
 * itself, and nothing downstream reads a result from this.
 *
 * 036 (issue 258, B3) — preferences are reset to the degraded fallback here too, for the same reason:
 * `adopt()`'s identity write persists `currentPreferences.get()` unconditionally, and nothing had
 * ever reset that module between one practitioner signing out and the next signing in. A new
 * practitioner whose own preferences read then failed would otherwise have the OUTGOING
 * practitioner's time zone, date style and theme silently written into their own
 * `completeme.identity` snapshot.
 */
function clear(): void {
  bumpEpoch();
  const outgoing = store.getState().account?.practitionerId ?? null;
  writeStored(null);
  clearIdentity();
  resetToSignedOut();
  resetPreferencesToFallback();
  // Issue 280: with no account in memory there is nobody to name — a cold launch whose stored token
  // had no identity pairing with it, refused by the server. FR-046 still applies, so every
  // practitioner database on the device has its views cleared (see `clearEveryStoredView`).
  const clearing = outgoing !== null ? clearStoredViewsFor(outgoing) : clearEveryStoredView();
  void clearing.catch((error: unknown) => {
    console.warn('session: could not clear stored views on sign-out', error);
  });
}

/**
 * This window's practitioner is no longer the one storage says is
 * here: replaced by a different practitioner signing in elsewhere, or signed out elsewhere. This
 * window's OWN session ends, but storage is untouched — whatever is there now belongs to a
 * session (or an absence of one) this window has no part in, and writing to it, even to remove it,
 * would be reaching into somebody else's browser tab. Never used for THIS window's own sign-out or
 * a refusal THIS window received; those are `clear()`'s. Used from BOTH `signed-in` and
 * `unconfirmed` (036, issue 219, the `storage` listener below) — an unconfirmed session is exactly
 * as much "this window's own" as a signed-in one, for the purpose of noticing it has been replaced.
 */
function endInMemoryOnly(): void {
  bumpEpoch();
  resetToSignedOut();
}

/**
 * `existingAccount` is what `adopt()` used to read off the store itself
 * (`store.getState().account ?? …`) — moved into the signature so the caller states what account
 * this call is FOR, rather than this function trusting whatever happens to be in the store the
 * instant it runs. `signIn()` never has one to give (`undefined`, forcing the `/me` read below).
 * `renewOnce()` captures it in the SAME breath as the epoch it checks against, before the network
 * round trip — so by the time this runs (after the epoch check below has already confirmed nothing
 * ended or replaced this session while the renewal was in flight), it is provably the account this
 * renewal was started for, not merely whatever the store happens to hold when this line executes.
 */
/**
 * 036 (issue 243, reviewer follow-up on `3fcdd26`) — does storage still agree with the pairing a
 * write is ABOUT to make, checked FRESH at write time rather than trusted from an earlier reading?
 * `sameStoredPractitioner` (below) is computed once, before `writeStored` even runs; the detached
 * re-pair write fires a hash-computation later, and the definitive write fires an account-and-
 * preferences round trip later still. Either gap is room enough for another tab's OWN sign-in to
 * land on the SAME shared storage keys — its `writeStored` rotating the token this tab is about to
 * pair, its own identity write replacing whatever this tab last saw there — and neither later write
 * has any way to notice that from `store.getState()` alone: that is THIS tab's own belief, not
 * what the browser's shared storage now holds. Re-reading storage here is the only way to see it.
 *
 * An unreadable key (`{ok: false}`) is nothing to disagree with — the same reading `renewOnce`'s
 * own mismatch check already gives it (its own comment explains why) — never a reason to withhold
 * a write this tab otherwise has every right to make.
 */
function stillSafeToPairIdentity(
  refreshToken: string,
  practitionerId: AccountSummary['practitionerId'],
): boolean {
  const storedToken = readStoredTagged();
  if (storedToken.ok && storedToken.value !== refreshToken) return false;
  const storedIdentity = readIdentity();
  if (storedIdentity.ok && storedIdentity.value !== null && storedIdentity.value.account.practitionerId !== practitionerId) {
    return false;
  }
  return true;
}

async function adopt(tokens: TokenPair, existingAccount?: AccountSummary | null): Promise<void> {
  /**
   * 036 (issue 243) — the practitioner this renewal is FOR, known synchronously, before any network
   * read: the caller states it (`existingAccount`, a warm renewal already validated by `renewOnce`'s
   * own mismatch check against the very identity sitting in storage) or it is whichever practitioner
   * this tab already believes it is (`store.getState().account` — a cold renewal recovering from
   * `restore()`'s own fast path, which validated that same pairing before ever starting this
   * renewal). `null` here means a genuine sign-in, or a cold start with nothing to carry forward —
   * the two cases this file has always had nothing trustworthy to show before the network answers,
   * unaffected by anything below.
   *
   * Cross-checked against whatever identity is ACTUALLY in storage right now, not merely trusted:
   * a `null` there is fine (nothing to disagree with — writing one now is a strict improvement), but
   * one naming a DIFFERENT practitioner (a race with another tab, however unlikely) is left alone,
   * falling through to the ORIGINAL clear-and-write-once behaviour below, exactly as safe as it
   * always was.
   */
  const trustedAccount = existingAccount ?? store.getState().account;
  const priorIdentityRead = readIdentity();
  const priorIdentity = priorIdentityRead.ok ? priorIdentityRead.value : null;
  const sameStoredPractitioner =
    trustedAccount !== null &&
    (priorIdentity === null || priorIdentity.account.practitionerId === trustedAccount.practitionerId);

  writeStored(tokens.refreshToken);

  // The token hash is started here, alongside the account/preferences reads below, so computing it
  // adds nothing to the critical path; awaited again (already settled by then) just before
  // `writeIdentity` at the end.
  const tokenHashRead = hashToken(tokens.refreshToken);

  if (sameStoredPractitioner && trustedAccount) {
    /**
     * 036 (issue 243) — a renewal for a practitioner already known here must never clear the
     * identity, even for the length of the account/preferences reads below. That gap — a valid
     * token with no identity paired to it at all, sometimes for as long as the five-second
     * session-traffic limit — is exactly what left an offline relaunch inside it unable to open
     * the stored lists (issue 243's own report). Re-paired as soon as the hash resolves — a
     * no-network hop, not the length of the reads below — rather than cleared in the meantime.
     *
     * Deliberately DETACHED from the rest of this call (`void`, no `await` here): every existing
     * caller of `adopt()` depends on the account/preferences reads being issued at THIS point,
     * immediately, with nothing on the wire ahead of them (`reading`'s own comment below says an
     * earlier draft of THIS port broke that once already, by awaiting the account before starting
     * the preferences read) — chaining this new write onto the critical path re-opened exactly the
     * kind of delay this fix exists to close, just measured in a hash instead of a round trip.
     * Reproduced and confirmed by running `session.store.renewal.spec.ts` with this awaited inline
     * first: FAIL, "expected exactly one GET .../me/preferences, found 0" — a plain `settle()` no
     * longer caught up to a preferences request the hash-await had pushed a tick later.
     *
     * Guarded against this call's own token no longer being the CURRENT one by the time the hash
     * resolves (this tab's OWN sign-out, or a new sign-in, running on the interleaved event queue
     * before the hash settles) — writing over a session that has already moved on would be exactly
     * the "reaching into a session that is no longer here" hazard this file's other identity writes
     * all guard against. ALSO re-checked against storage itself (`stillSafeToPairIdentity`), not
     * only this tab's own in-memory belief: `sameStoredPractitioner` above was read before
     * `writeStored` even ran, a hash-computation before this callback fires, and nothing stops
     * another tab's own sign-in landing on the shared keys in that gap — the race 3b's own defences
     * exist for, found in review of this very fix. `trustedAccount` and the CURRENT preferences
     * (already the ones last confirmed, from the prior `adopt()` or from `restore()`'s own fast
     * path) are a BRIDGE, not the final word — both are written again, unconditionally, by the
     * definitive write at the end of this same call (guarded the same way), which — attached to the
     * SAME `tokenHashRead` promise, but registered later — is guaranteed to resolve after this one,
     * per promise callback ordering, never before it.
     */
    const knownAccount = trustedAccount;
    void tokenHashRead
      .then((hash) => {
        if (store.getState().refreshToken !== tokens.refreshToken) return;
        if (!stillSafeToPairIdentity(tokens.refreshToken, knownAccount.practitionerId)) return;
        writeIdentity({ account: knownAccount, preferences: currentPreferences.get(), tokenHash: hash });
      })
      .catch((error: unknown) => {
        console.warn('adopt: could not compute the pairing hash early; the identity is re-paired once the reads settle instead', error);
      });
  } else {
    /**
     * Cleared in the SAME synchronous breath as the token above, with no `await`
     * between them. Without this, a fresh token can sit beside a STALE identity for the length of
     * the account/preferences reads below: another window renewing at that exact moment would read
     * a token for the practitioner signing in HERE beside the identity of whoever was signed in
     * here before. Clearing rather than guessing the new identity early is deliberate — the real
     * identity is not known until `loadedAccount` resolves, and "no identity" is the value
     * `renewOnce`'s mismatch check already treats as "not safe to adopt", where a stale WRONG one
     * would not be. Reached only for a genuine sign-in, or a race this call cannot vouch for
     * (`sameStoredPractitioner` false) — never for an ordinary renewal, which is the branch above.
     */
    clearIdentity();
  }
  store.setState({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });

  // The account changes only through the practitioner's own hands: fetching it on every silent
  // renewal would put a round trip on the critical path of every page load for nothing, and the one
  // place it CAN change without a sign-out — /account saving PATCH /me (014 FR-029) — hands the
  // server's answer to adoptAccount() below, so the cache is replaced at the moment it goes stale
  // rather than re-read on a schedule.
  const reading =
    existingAccount ??
    directClient().get<AccountSummary>(url('/api/v1/me'), {
      headers: { Authorization: `Bearer ${tokens.accessToken}` },
      timeoutMs: SESSION_TIMEOUT_MS,
    });

  /**
   * 009 FR-011: preferences must be available before the first render of any screen that depends on
   * them, and this is the only place that can promise it — `restore()`, `signIn()` and `renewOnce()`
   * all pass through here, and nothing else establishes a session. Awaited before `status` flips, so
   * no screen can paint a default and then correct itself (SC-009).
   *
   * Unlike the account it is **not** guarded by what is already held. FR-011 has the read happen on
   * each sign-in *and* each session renewal, and FR-012 names the next renewal as the only way a
   * degraded session recovers — a guard here would make that recovery unreachable. The cost a guard
   * would otherwise protect against is handled in the preferences store instead: a failed re-read
   * keeps the values already held rather than replacing them with the defaults.
   *
   * On `directClient`, with an explicit bearer, for the same reason the account read is: a 401
   * through the renewing wrapper would re-enter the renewal this may be running inside.
   */
  // ISSUED ALONGSIDE THE ACCOUNT, not after it: they are independent reads and there is no reason to
  // put them on the wire one at a time. Both promises are started before either is awaited — an
  // earlier draft of this port awaited the account first and quietly added a round trip to the
  // critical path of every page load.
  const preferencesRead = loadPreferences(tokens.accessToken, directClient());

  const loadedAccount = await reading;
  // FR-012: a preferences read that fails must not take sign-in down with it. `loadPreferences`
  // resolves either way and says so through `isDegraded`; this await is for the ordering, not the
  // result.
  await preferencesRead;

  // 015 FR-011: the theme is in effect from the first authenticated paint, which means the attribute
  // must be written BEFORE `status` flips — the theme spec records the status at the instant of the
  // write. A degraded read applies the fallback's light (FR-012).
  //
  // Absorbed rather than propagated, and the reason is the line at the top of this function: the
  // refresh token has ALREADY been rotated and stored. Anything that throws past that point spends a
  // token and then fails, and `renewOnce`'s catch reads the failure as a refused renewal and calls
  // `clear()` — a permanent sign-out, since the spent token cannot be replayed. `applyTheme` throws
  // on a spelling this client does not know (a later release's word reaching an older cached
  // bundle), which is worth a wrong paint and not worth a session. The seam still throws for every
  // other caller; only a session past the point of no return declines to die of it.
  try {
    applyTheme(preferredTheme.get());
  } catch {
    applyTheme('light');
  }

  store.setState({ account: loadedAccount, status: 'signed-in' });

  /**
   * "nothing in `adopt()` after the token is stored may throw past it," made
   * structural rather than trusted to each addition here individually. `hashToken` no longer throws
   * on a missing `crypto.subtle` (it resolves `null` instead — see its own comment), which was the
   * ONE way this specific tail used to fail; this wrap is the defensive backstop besides, so a LATER
   * change to what `writeIdentity` is given cannot reintroduce the same class of defect by forgetting
   * this file's own history with it.
   */
  try {
    // 036 R-003: written last, from what was just adopted — never restated from a literal, so this
    // can never disagree with the account or preferences the rest of this function just settled on.
    // `tokenHash` pairs this identity to the EXACT token this call wrote — provably, not just by
    // writing them close together (or `null` where no hash could be computed at all).
    //
    // 036 (issue 243, reviewer follow-up) — this write is exactly as exposed to another tab's own
    // sign-in landing on the shared keys as the detached one above, and for longer: everything
    // between here and `writeStored` at the top is a real account-and-preferences round trip, not a
    // hash. That exposure PRE-DATES this issue (the original, unconditional write had it too — see
    // `3fcdd26`'s evidence); re-checking storage here is the same guard this file already needed for
    // the detached write, applied where it was always missing.
    const hash = await tokenHashRead;
    if (stillSafeToPairIdentity(tokens.refreshToken, loadedAccount.practitionerId)) {
      writeIdentity({ account: loadedAccount, preferences: currentPreferences.get(), tokenHash: hash });
    }
  } catch (error) {
    console.warn('adopt: could not compute or store the identity pairing record; the session still adopts', error);
  }
}

/** At most one renewal at a time (005 FR-008) — including the one startup begins. */
let inFlight: Promise<boolean> | null = null;

/**
 * 036 T036 (research R-004, F-008) — the cross-WINDOW half of "at most one renewal at a time".
 * `inFlight` above already serialises calls made from within one window's own module state; it says
 * nothing about a second tab, which has its own copy of that variable. The refresh token rotates on
 * every renewal, so two windows racing it today present the SAME token to the server: one wins, the
 * other gets a 401 and — before this task — read that as a refusal and signed out.
 *
 * `navigator.locks` does not exist in jsdom, hence the seam; production never skips the lock
 * silently — an unavailable `navigator.locks` still runs the callback, but says so once.
 *
 * "Second definitions to remove" (redesign) — this used to implement that fallback inline, exactly
 * as `core/outbox/outbox.store.ts` separately did for its own send-loop lock. Both now call
 * `core/concurrency/browser-lock.ts`'s `createLockRunner`, which writes the fallback ONCE; each
 * caller still gets its OWN runner instance (its own `warned` flag, its own test seam) so a test
 * that installs a custom runner for one does not silently also govern the other's calls.
 */
const RENEW_LOCK_NAME = 'completeme.session.renew';

const renewLock = createLockRunner('session');

/** Test seam. Production code never calls it. */
export function setRenewLockRunner(next: LockRunner | null): void {
  renewLock.setLockRunner(next);
}

/**
 * Best effort only: this window has already decided the tokens are NOT this session's to keep, so
 * there is nobody left to tell if the server cannot be reached either — the record is that the
 * refresh token was never adopted here, not that it was successfully revoked everywhere.
 */
function revokeAbandonedTokens(refreshToken: string): void {
  directClient()
    .post(url('/api/v1/auth/sign-out'), { refreshToken }, { timeoutMs: SESSION_TIMEOUT_MS })
    .catch(() => {
      /* nothing to do: this window never adopted the token, and cannot make the server hear it either */
    });
}

/**
 * `restore()` (startup — nothing in memory, adopt whatever storage holds) and `renew()` (the 401
 * path, and anything else mid-session) used to be one function reached by two callers, distinguished
 * only by whether an account happened to be in memory. That was the isolation gap: a WARM renewal
 * that had just ended its OWN session in memory (the mismatch branch below, or a prior call to it)
 * left `inMemoryPractitionerId === null` — indistinguishable, by that reading alone, from a genuine
 * cold start — so a SECOND 401 arriving right after adopted whatever storage held next, cold-fashion,
 * with nobody's session live to check it against.
 *
 * The two are now told apart by which caller asked, not by what is in memory. `restore()` is COLD:
 * it always adopts what storage holds, lock included, exactly as before. `renew()` is WARM: with no
 * account in memory it has nothing of this tab's to renew, and returns `false` at once — no storage
 * read, no lock — leaving `restore()` as the only door back in from nothing.
 */
type RenewalKind = 'cold' | 'warm';

async function renewOnce(kind: RenewalKind): Promise<boolean> {
  if (kind === 'warm' && store.getState().account === null) return false;

  return renewLock.withLock(RENEW_LOCK_NAME, async () => {
    // Re-checked INSIDE the lock: a warm caller that queued behind another one holding the lock may
    // find its own session gone (ended by whatever the earlier holder just did) by the time it runs.
    if (kind === 'warm' && store.getState().account === null) return false;

    /**
     * FIRST, INSIDE THE LOCK, RE-READ THE STORED TOKEN (R-004) — AND THE STORED IDENTITY.
     * Preferring storage over memory is the whole of R-004's fix, and it opened a second
     * hazard by itself: storage might now belong to a DIFFERENT practitioner than the one this
     * window is renewing for — A signed out and B signed in, in another window, while this
     * window's access token was expiring. Presenting B's token under A's `account` would rename
     * A's queued captures into B's device database.
     *
     * So the token is never taken at face value: it is only ADOPTED when the stored identity says
     * it belongs to the SAME practitioner this window already believes it is AND names a
     * `tokenHash` that matches the token actually sitting beside it — the PROVABLE-pair check, not
     * just "the fields happen to agree."
     *
     * 036 (issue 258, B1) — this used to read `kind === 'warm' ? … : null`, skipping the whole check
     * for a COLD renewal even when `restore()`'s own fast path had already put an account in memory
     * (the `unconfirmed` screen). That gap is exactly what let a cold renewal pair that carried-over
     * account with ANOTHER practitioner's token: `adopt()`'s own `trustedAccount` falls back to
     * `store.getState().account` whenever its caller passes `null` (`existingAccount ??
     * store.getState().account` — `??` does not distinguish "explicitly nothing" from "nothing
     * supplied"), and nothing upstream of it had re-validated that account against storage for the
     * COLD path the way it already does for a warm one. The scenario: tab 1 cold-launches holding A's
     * paired identity (fast path sets `account: A`, `unconfirmed`); tab 2 signs in as B, landing on
     * the SAME shared storage keys; tab 1's cold renewal, with this check skipped, read B's token,
     * renewed it, and `adopt()` paired A's account with B's new token. Reading `inMemoryPractitionerId`
     * from the store UNCONDITIONALLY closes it: a genuine cold start (nothing in memory) still reads
     * `null` here and skips the block exactly as before — nothing changes for that case — but a cold
     * renewal recovering from the fast path now gets the SAME re-read-storage-at-lock-time validation
     * a warm renewal always had, and a mismatch ends this tab's session in memory (`endInMemoryOnly()`
     * below) rather than ever reaching `adopt()` with a stale, unvalidated account. `accountAtStart`
     * (below) is deliberately UNCHANGED — still `null` for a cold renewal — so issue 219's own
     * guarantee (the first renewal this window makes always fetches `/me` fresh) still holds; this
     * fix only ever PREVENTS a bad pairing, it never grants a cold renewal a new way to skip the read.
     *
     * The mismatch check itself is skipped, not failed, when storage
     * cannot be read AT ALL (`{ok: false}`): there is nothing to compare against, and the safe
     * reading of "unreadable" is "unconfirmed," not "signed out elsewhere." Falls through to renew
     * with whatever this window already holds in memory, exactly as every renewal did before this
     * cycle's isolation fix.
     */
    const inMemoryPractitionerId = store.getState().account?.practitionerId ?? null;
    const storedTokenRead = readStoredTagged();
    const storedIdentityRead = readIdentity();

    if (inMemoryPractitionerId !== null && storedTokenRead.ok && storedIdentityRead.ok) {
      const storedToken = storedTokenRead.value;
      const storedIdentity = storedIdentityRead.value;
      const samePractitioner = storedToken !== null && storedIdentity !== null
        && storedIdentity.account.practitionerId === inMemoryPractitionerId;
      const stillThisPractitioner = samePractitioner
        && (await hashMatches(storedToken, storedIdentity.tokenHash));
      if (!stillThisPractitioner) {
        // Either signed out elsewhere (no stored token, or no stored identity), replaced by a
        // different practitioner signing in elsewhere (a stored identity that names someone else),
        // or a torn write (fields agree, hash does not) — treated exactly the same way: neither
        // storage nor either practitioner's device database is this window's to touch.
        endInMemoryOnly();
        return false;
      }
    }

    const refreshToken = (storedTokenRead.ok ? storedTokenRead.value : null) ?? store.getState().refreshToken;
    if (!refreshToken) {
      clear();
      return false;
    }

    /**
     * Captured HERE, before the network round trip, alongside the account this call is renewing
     * for — so that when the answer comes back, "did anything end or replace this session while we
     * were waiting" is a plain equality check rather than a guess from whatever the store happens to
     * hold at that moment (a sign-out or a different practitioner's sign-in
     * landing while this request was in flight, answered only after both had already happened).
     *
     * 036 (issue 219): `accountAtStart` — the account `adopt()` is told to trust rather than
     * re-fetch — is taken from the store only for a WARM renewal. A cold one always passes `null`,
     * even when `restore()`'s own fast path already populated `account` (the `unconfirmed` screen):
     * that account was never CONFIRMED by this window asking the server, only carried over from
     * `completeme.identity`, so the first real renewal this window makes still fetches `/me` fresh —
     * exactly as a genuine cold start always has.
     */
    const epochAtStart = sessionEpoch;
    const accountAtStart = kind === 'warm' ? store.getState().account : null;

    try {
      const tokens = await directClient().post<TokenPair>(
        url('/api/v1/auth/renew'),
        { refreshToken },
        { timeoutMs: SESSION_TIMEOUT_MS },
      );
      // issue 208/219: a renewal's own outcome is exactly as real a reachability signal as any other
      // request, so `core/reach/` hears it too — `sessionTraffic: true` keeps it out of the bad-row
      // rule's evidence counter (`reach.store.ts`'s own comment on the flag says why).
      noteOutcome(200, { sessionTraffic: true });
      if (sessionEpoch !== epochAtStart) {
        // This tab's session moved on while the request was in flight. The tokens answer FOR THE
        // SESSION THIS CALL STARTED WITH, which no longer holds — adopting them now would be exactly
        // the cross-practitioner pairing this file exists to prevent, just arriving a different way.
        // Nothing is written to storage or the store; the spent-looking new refresh token is revoked
        // best-effort so it is not left valid and unused.
        revokeAbandonedTokens(tokens.refreshToken);
        return false;
      }
      await adopt(tokens, accountAtStart);
      return true;
    } catch (error) {
      if (error instanceof HttpFailure) noteOutcome(error.status, { sessionTraffic: true });
      if (sessionEpoch !== epochAtStart) {
        // The session this call was renewing for has already ended or been replaced by something
        // else — touching storage or the store now (via `standDown()`/`clear()` below) would act on
        // whatever the CURRENT session left there, not this stale call's own.
        return false;
      }
      // A renewal that could not be ASKED is not a renewal that was REFUSED. Keep the token; the
      // session is unconfirmed, not revoked, and it will restore itself on the next load with a
      // connection (021 FR-006b).
      if (couldNotReach(error)) {
        standDown();
        return false;
      }
      clear();
      return false;
    }
  });
}

function renewShared(kind: RenewalKind): Promise<boolean> {
  inFlight ??= renewOnce(kind).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** The WARM path — see `renewOnce`'s own comment for what that means and why it is not the cold one. */
export function renew(): Promise<boolean> {
  return renewShared('warm');
}

/**
 * 036 (issue 219) — keeps `completeme.identity`'s account/preferences snapshot from going stale
 * relative to what THIS tab has just confirmed with the server, without touching the token pairing
 * at all (the `tokenHash` carried forward is the one already there — nothing here has rotated a
 * token, so recomputing it would be wasted work and a needless async hop).
 *
 * Guarded by the SAME practitioner check every other identity write in this file makes: if the
 * stored identity no longer names this tab's own practitioner (signed out or replaced elsewhere,
 * between the change that triggered this and this call actually running), it is not this tab's to
 * touch — silently does nothing, exactly like `endInMemoryOnly()`'s own reasoning for leaving
 * storage alone.
 */
function refreshStoredIdentitySnapshot(nextAccount: AccountSummary, nextPreferences: PreferencesSummary): void {
  const existing = readIdentity();
  if (!existing.ok || existing.value === null) return;
  if (existing.value.account.practitionerId !== nextAccount.practitionerId) return;
  writeIdentity({ account: nextAccount, preferences: nextPreferences, tokenHash: existing.value.tokenHash });
}

/**
 * 014 FR-029: /account's save answers with the account summary the server now holds; adopting it
 * here is what makes the toolbar's identity change without a sign-out and without a reload. A
 * replacement, never a merge — the server's summary is whole by contract.
 */
export function adoptAccount(next: AccountSummary): void {
  store.setState({ account: next });
  refreshStoredIdentitySnapshot(next, currentPreferences.get());
}

/**
 * 036 (issue 219) — the other half of keeping the stored snapshot fresh: a preferences save changes
 * the LIVE store at once (015 FR-013) but, without this, never touched `completeme.identity` until
 * the next renewal. `onPreferencesSaved` fires only for a genuine save, never for an ordinary load or
 * `restore()`'s own fast-path adoption, so this never fires for a reason this file itself caused.
 */
onPreferencesSaved((values) => {
  const current = store.getState();
  if (current.status !== 'signed-in' && current.status !== 'unconfirmed') return;
  if (current.account === null) return;
  refreshStoredIdentitySnapshot(current.account, values);
});

export async function signIn(login: string, password: string): Promise<void> {
  // The start of a new session: a renewal already in flight for whatever this tab was
  // before — signed out, or signed in as someone else — must not be adopted once this one begins.
  bumpEpoch();
  const body: SignInRequest = { loginName: login, password };
  const tokens = await directClient().post<TokenPair>(
    url('/api/v1/auth/sign-in'),
    body,
    { timeoutMs: SESSION_TIMEOUT_MS },
  );
  await adopt(tokens);
}

/**
 * Called once at startup: turn a persisted refresh token back into a session, or settle as signed
 * out. Goes through the SAME shared single-flight slot `renew()` uses, so a restore racing an
 * interceptor-driven renewal still spends the rotating refresh token only once — whichever call
 * arrives first starts it, and the other simply awaits that same promise, cold or warm.
 *
 * 036 (issue 219, research R-003/R-006) — TWO shapes now, not one:
 *
 * A stored token AND a stored identity that PAIR (the same provable check `renewOnce`'s own
 * mismatch guard uses): nothing trustworthy has to wait for the network. The account and
 * preferences last confirmed are put into effect at once, SYNCHRONOUSLY, before the renewal that
 * confirms them is even started — `firstPaintReady()` below is what `main.tsx` actually awaits for
 * its first render, and it settles the moment `status` leaves `unknown`, which happens right here,
 * long before this function itself returns.
 *
 * This function's OWN promise still resolves only once the renewal it starts has settled —
 * `signed-in`, `signed-out`, or left `unconfirmed` where `renewOnce`'s own outcomes already say —
 * exactly as it always has for every existing caller. Deliberately NOT split into "resolve early,
 * fire the renewal in the background": every test in this codebase that calls `restore()` — this
 * cycle's own new ones included — awaits it expecting the SESSION it started to be settled by the
 * time it returns, `session.store.isolation.spec.ts`'s cold-restore cases among them, and a
 * fire-and-forget renewal would leave `restore()`'s own promise resolved long before `adopt()`'s
 * chain (the account, then the preferences, then the identity write) has actually finished —
 * correct for `main.tsx`, which no longer waits on it, but wrong for every caller that still does.
 *
 * Anything else with a token (no identity at all, or one that does not pair — a foreign or torn
 * write) has nothing trustworthy to show: this is the ORIGINAL cold path, unchanged, so a genuine
 * cold start is confirmed or refused before `firstPaintReady()` settles either.
 */
export async function restore(): Promise<void> {
  const tokenRead = readStoredTagged();
  const token = tokenRead.ok ? tokenRead.value : null;
  if (!token) {
    store.setState({ status: 'signed-out' });
    return;
  }

  const identityRead = readIdentity();
  const identity = identityRead.ok ? identityRead.value : null;

  if (identity && (await hashMatches(token, identity.tokenHash))) {
    adoptStoredPreferences(identity.preferences);
    try {
      applyTheme(preferredTheme.get());
    } catch {
      applyTheme('light');
    }
    // The account and token are put in the store FIRST, then `standDown()` — the SAME function a
    // renewal that could not be asked reaches for — settles `unconfirmed` from them. Reused rather
    // than restated: two definitions of "keep the account, retire the access token, unconfirmed"
    // would drift the day one of them changes, and `firstPaintReady()` resolves the instant
    // `standDown()`'s own `store.setState` runs, well before the renewal below is even asked for.
    store.setState({ refreshToken: token, account: identity.account });
    standDown();
  }

  await renewShared('cold');
}

/**
 * 036 (issue 219, research R-006, criterion 2) — what `main.tsx` awaits for its FIRST RENDER,
 * instead of `restore()` itself. `restore()` keeps ONE contract for every other caller — it
 * resolves once the session it started is fully settled — which is what lets it be a fire-and-await
 * primitive everywhere else in this file and in every test that calls it. But `unconfirmed` (or
 * `signed-out`, on the no-token and no-identity paths) is set SYNCHRONOUSLY, before the confirming
 * renewal is even asked for, and criterion 2 needs the first paint to start the instant THAT
 * happens — not after the renewal behind it eventually settles, which can take up to R-006's
 * five-second limit against a stalled connection.
 *
 * Resolves at once if `status` has already left `unknown` by the time it is called; otherwise the
 * first transition away from `unknown` resolves it. Never rejects, and never resolves twice.
 */
export function firstPaintReady(): Promise<void> {
  if (store.getState().status !== 'unknown') return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = store.subscribe((state) => {
      if (state.status !== 'unknown') {
        unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * The one primitive that ends a session at the API and in storage — no knowledge of the outbox at
 * all. `core/session/sign-out.ts`'s `signOut()` is what practitioner-facing "sign out" means
 * (send first, ask if something is left); this is what it calls once it has decided to actually
 * end the session, and it is also what a caller that must NOT consult the outbox uses directly —
 * `accounts-page.tsx`'s self-deactivation: an account that just deactivated
 * itself can send nothing, and FR-050 says a session that ends without the practitioner choosing
 * to discard anything leaves unsent items in place.
 */
export async function signOutNow(): Promise<void> {
  bumpEpoch();
  const refreshToken = store.getState().refreshToken ?? readStored();
  clear();
  if (refreshToken) {
    // Ending it at the API is the point (005 FR-006); a failure here must not strand the user signed in.
    try {
      await directClient().post(
        url('/api/v1/auth/sign-out'),
        { refreshToken },
        { timeoutMs: SESSION_TIMEOUT_MS },
      );
    } catch {
      /* already gone as far as this browser is concerned */
    }
  }
}

/** Test seam, like the preferences store's. Production code never calls it. */
export function resetSession(): void {
  store.setState({ ...initial });
  inFlight = null;
  // `sessionEpoch` is deliberately NOT reset here. It is only ever compared to a value captured
  // locally at the start of one renewal, never to a fixed constant, so carrying it forward across
  // tests changes nothing about what a comparison means — but resetting it to a fixed number WOULD:
  // a test that ends one session (bumping the epoch forward) and then called this before starting
  // the next could land back on the exact number a stale renewal captured before the reset, making
  // two genuinely different sessions compare equal by coincidence.
}

/**
 * 036 (issue 219, research R-006) — what `core/http/renewing-client.ts` needs to know: is a renewal
 * ALREADY on the wire, right now, for this tab? A FUNCTION, not the `inFlight` variable itself —
 * that stays private so nothing outside this file can read a stale reference after it settles, or,
 * worse, assign to it. `null` means no renewal is in flight; otherwise the same promise `renew()`
 * or `restore()` are themselves awaiting.
 *
 * Exists so a request that is about to go out with NO access token — the moment right after
 * `restore()`'s fast path sets `unconfirmed`, before its background renewal has answered — can wait
 * for that renewal instead of being sent bare, refused with a 401 nobody asked for, and only then
 * discovering the very renewal already running. Without this, every request in flight at that
 * moment would cost TWO round trips instead of one, which is exactly what
 * `e2e/home-scale.spec.ts`'s fixed count would catch.
 */
export function pendingRenewal(): Promise<boolean> | null {
  return inFlight;
}

/**
 * 036 (issue 219, FR-045) — another tab's write to `completeme.identity` reaching this one. A
 * same-window write never fires a `storage` event at all (the event is defined to skip the document
 * that made the change), so every firing here is genuinely "elsewhere, on this browser".
 *
 * `adopt()` clears the stored identity and rewrites it moments later — its own comment explains why
 * (a fresh token must never sit beside a stale identity, even for the length of the account and
 * preferences reads). That gap fires a `storage` event of its own, `newValue: null`, for a renewal
 * that is not ending anything at all — reacting to it as a sign-out would be exactly the "torn
 * write" isolation hazard `renewOnce`'s own pairing check exists to close, arriving by a different
 * door. The refresh token is what tells the two apart: `clear()` — a genuine, settled sign-out —
 * removes BOTH together, synchronously, so the token is already gone too by the time this fires;
 * `adopt()` writes the NEW token FIRST, so the token is still present during its own identity-
 * clearing gap. A `null` identity beside a PRESENT token is read as "wait for the rewrite", never as
 * "ended" — the follow-up event, once the rewrite lands, is what actually decides it (by then either
 * the same practitioner, which does nothing, or a different one, which does).
 */
function onIdentityStorageChange(event: StorageEvent): void {
  if (event.key !== identityKey()) return;
  const current = store.getState();
  if (current.status !== 'signed-in' && current.status !== 'unconfirmed') return;

  if (event.newValue === null) {
    const stillHasToken = readStoredTagged();
    if (stillHasToken.ok && stillHasToken.value !== null) return; // a renewal's transient gap elsewhere
    endInMemoryOnly();
    return;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(event.newValue) as unknown;
  } catch {
    return; // not a value this tab can safely read as an identity
  }
  if (!isStoredIdentity(parsed)) return;
  const ownId = current.account?.practitionerId ?? null;
  if (parsed.account.practitionerId !== ownId) endInMemoryOnly();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', onIdentityStorageChange);
}

let unsubscribeSessionBeat: (() => void) | null = null;

/**
 * 036 (issue 219, research R-007) — the WARM half of recovering from `unconfirmed` without a
 * reload and without a navigation (criterion 3). Joins `core/reach/`'s one shared beat: the `online`
 * event, the document becoming visible, and its five-second poll while there is something to retry.
 *
 * `renew()` is single-flight (`inFlight`) and is already a no-op — no storage read, no lock, no
 * request — the instant `account` is `null` (`renewOnce`'s own warm guard), so a beat that fires
 * while genuinely signed out costs nothing; and while already `signed-in` it simply renews a session
 * that did not need it, which `attempt` below avoids by checking `status` itself rather than relying
 * on that guard alone. Only `unconfirmed` ever starts a real attempt here.
 *
 * Called once, from `main.tsx`, the same way `startOutboxWatch` is — `onSignedIn` (already wired in
 * `core/outbox/outbox.store.ts`) is what wakes the outbox the moment a recovery succeeds.
 */
export function startSessionRecovery(): void {
  if (unsubscribeSessionBeat || typeof window === 'undefined') return;
  const attempt = (): void => {
    if (store.getState().status === 'unconfirmed') void renew();
  };
  unsubscribeSessionBeat = subscribeToBeat({
    hasWork: () => store.getState().status === 'unconfirmed',
    onBeat: attempt,
  });
}

/** Test seam, like `resetOutbox`'s own beat teardown. Production code never calls it. */
export function resetSessionRecovery(): void {
  unsubscribeSessionBeat?.();
  unsubscribeSessionBeat = null;
}
